import { AUTH_URL } from "../constants";
import { AUTH_KEY } from "../context/authContext";
import { APIRequest, HTTPClient, RequestType } from "../services/http_client";
import LocalStorageService from "../services/localstorage.service";
import { Attachment, AttachmentType, AttachmentUpload } from "../types/attachment";

const client = new HTTPClient()

interface ResponseType {
    file?: Attachment;
    message?: string;
    error?: string;
}

interface RawResponse {
    success: boolean;
    error: any;
  }

export interface FileFilers {
    type: string;
    page?: number;
    limit?: number;
    title?: string;
    source?: string;
    foot?: string;
}

export interface ResponseArrayType {
    files?: Array<Attachment>;
    totalItems?: number;
    error?: string;
}

const getEndpoint = (fileType: AttachmentType) => {
    const endpoints = {
        [AttachmentType.IMAGE]: '/image',
        [AttachmentType.AUDIO]: '/audio',
        [AttachmentType.VIDEO]: '/video',
        [AttachmentType.PDF]: '/pdf'
    }
    return endpoints[fileType] || '';
}

const encodeFilters = (filters: FileFilers) => {
    const encodedFilters = new URLSearchParams()
  
    Object.keys(filters).forEach((filter: any) => {
      const filterKey = filter as keyof FileFilers;
      if(filters[filterKey] !== undefined){
        encodedFilters.append(filter, filters[filterKey]?.toString() ?? '')
      }
    })
  
    return encodedFilters.toString()
  }

class FilesModel {
    static async deleteFile(fileId: number) {
        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            error: "Unauthorized"
        }

        const request = new APIRequest(
            RequestType.DELETE,
            `${AUTH_URL}/files/${fileId}`,
            undefined,
            auth.accessToken
          )
    
          const response = await client.delete(request)
    
          if (!response.success) {
              return {
                  error: response.errorMessage,
              }
          }
    
          return response.result as unknown as RawResponse
    }

    static async getFiles(filters: FileFilers): Promise<ResponseArrayType> {
        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            error: "Unauthorized"
        }

        const request = new APIRequest(
            RequestType.GET,
            `${AUTH_URL}/files?${encodeFilters(filters)}`,
            undefined,
            auth.accessToken,
          )
    
          const response = await client.get(request)
    
          if (!response.success) {
              return {
                  error: response.errorMessage,
              }
          }
    
          return response.result as unknown as ResponseArrayType
    }

    static async updateFile(fileData: AttachmentUpload): Promise<ResponseType> {
        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            error: "Unauthorized"
        }

        const endpoint = AUTH_URL + getEndpoint(fileData.type)
        if(endpoint.length === 0) return {
            error: "No se puede procesar el tipo de archivo"
        }

        const request = new APIRequest(
            RequestType.PUT,
            `${endpoint}/${fileData.id}`,
            JSON.stringify(fileData),
            auth.accessToken,
            {},
            900000
          )
    
          const response = await client.put(request)
    
          if (!response.success) {
              return {
                  error: response.errorMessage,
              }
          }
    
          return response.result as unknown as ResponseType
    }

    static async uploadFile(file: any, fileData: AttachmentUpload): Promise<ResponseType> {
        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            error: "Unauthorized"
        }

        const form = new FormData()
        form.append("file", file)
        Object.keys(fileData).forEach((item: any) => {
            form.append(item, fileData[item])
        })

        const endpoint = AUTH_URL + getEndpoint(fileData.type)
        if(endpoint.length === 0) return {
            error: "No se puede procesar el tipo de archivo"
        }

        const request = new APIRequest(
            RequestType.POST,
            endpoint,
            form,
            auth.accessToken,
            {},
            900000
          )
    
          const response = await client.post(request)
    
          if (!response.success) {
              return {
                  error: response.errorMessage,
              }
          }
    
          return response.result as unknown as ResponseType
    }
}

export default FilesModel